/**
 * Per-viewer preferences in localStorage: theme, layout, volume, panel sizes.
 *
 * Storage can be blocked or cleared (private windows, locked-down machines), so
 * every read falls back to a default and every write is allowed to fail. These
 * are conveniences; nothing here may be the only copy of something that matters.
 */

import { useCallback, useEffect, useState } from "react";

export function readPref<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writePref<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not remembered; the app still works.
  }
}

/** useState that remembers itself. */
export function usePref<T>(key: string, fallback: T): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readPref(key, fallback));
  useEffect(() => writePref(key, value), [key, value]);
  const set = useCallback((next: T | ((prev: T) => T)) => setValue(next), []);
  return [value, set];
}
